import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Language, translations, TranslationKeys } from '@/i18n/translations';

const STORAGE_KEY = 'dealigator-language';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const detectBrowserLanguage = (): Language => {
  if (typeof navigator === 'undefined') return 'en';
  const browserLang = navigator.language || (navigator as any).userLanguage;
  if (!browserLang) return 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  if (langCode === 'ru') return 'ru';
  return 'bg';
};

const getInitialLanguage = (): Language => {
  if (typeof localStorage === 'undefined') return detectBrowserLanguage();
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && ['en', 'bg', 'ru'].includes(stored)) {
    return stored as Language;
  }
  return detectBrowserLanguage();
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => getInitialLanguage());

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }
  }, []);

  useEffect(() => {
    if (typeof localStorage !== 'undefined' && !localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, language);
    }
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

const fallback: LanguageContextType = {
  language: 'bg',
  setLanguage: () => {},
  t: translations['bg'],
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  return context ?? fallback;
};
